import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Icon from "@mui/material/Icon";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { GetProfessorsCall, GetUniversityProfsCall } from "../../redux/api/professor.api";
import {
  AddUniversityUsersCall,
  EditUniversityUserCall,
  RemoveUniversityUserCall,
  GetUniversitiesCall,
  ResendInvitationCall,
  ResendCredentialsCall,
} from "../../redux/api/university.api";

function Professors() {
  const auth = useSelector((state) => state?.auth);
  const isUniversityAdmin = auth?.role === "university" || auth?.role === "university_admin";
  const universityId = auth?.university_id ?? auth?.universityInfo?.id;

  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuProfessor, setActionMenuProfessor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [professorToDelete, setProfessorToDelete] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", university_id: "" });
  const [universities, setUniversities] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  const openActionMenu = (e, professor) => {
    setActionMenuAnchor(e.currentTarget);
    setActionMenuProfessor(professor);
  };
  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuProfessor(null);
  };

  const handleResendInvitation = async () => {
    if (!actionMenuProfessor) return;
    const profId = actionMenuProfessor.id;
    closeActionMenu();
    setError("");
    setSuccessMessage("");
    const res = await ResendInvitationCall(profId);
    const data = res?.data ?? res;
    if (data?.email_sent) {
      setSuccessMessage("Invitation email sent.");
    } else if (data?.email_error) {
      setError(`Email could not be sent: ${data.email_error}`);
    } else if (res?.status >= 400 || data?.message) {
      setError(data?.message || "Failed to resend invitation");
    } else {
      setSuccessMessage("Invitation email sent.");
    }
  };

  const handleResendCredentials = async () => {
    if (!actionMenuProfessor) return;
    const profId = actionMenuProfessor.id;
    closeActionMenu();
    setError("");
    setSuccessMessage("");
    const res = await ResendCredentialsCall(profId);
    const data = res?.data ?? res;
    if (data?.email_sent) {
      setSuccessMessage("New credentials email sent with a new temporary password.");
    } else if (data?.email_error) {
      setError(`New password was set but email could not be sent: ${data.email_error}`);
    } else if (res?.status >= 400 || data?.message) {
      setError(data?.message || "Failed to resend credentials");
    } else {
      setSuccessMessage("New credentials email sent.");
    }
  };

  const resetForm = () => {
    setForm({
      first_name: "",
      last_name: "",
      email: "",
      university_id: isUniversityAdmin ? universityId : "",
    });
    setEditing(null);
  };

  const openAddDialog = async () => {
    resetForm();
    if (!isUniversityAdmin) {
      const list = await GetUniversitiesCall();
      setUniversities(list || []);
    }
    setDialogOpen(true);
  };

  const openEditDialog = (professor) => {
    setForm({
      id: professor.id,
      first_name: professor.first_name || professor.full_name?.split(" ")[0] || "",
      last_name: professor.last_name || professor.full_name?.split(" ").slice(1).join(" ") || "",
      email: professor.email || "",
    });
    setEditing(professor);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);
  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const fetchProfessors = async () => {
    try {
      setLoading(true);
      setError("");
      const data =
        isUniversityAdmin && universityId
          ? await GetUniversityProfsCall(universityId)
          : await GetProfessorsCall(universityId || null);
      setProfessors(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Failed to load professors");
      setProfessors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfessors();
  }, [isUniversityAdmin, universityId]);

  const handleSubmit = async () => {
    const email = (form.email || "").trim();
    if (!email) {
      setError("Email is required");
      return;
    }
    const universityIdForAdd = isUniversityAdmin ? universityId : (form.university_id || "").trim();
    if (!editing && !universityIdForAdd) {
      setError("University is required");
      return;
    }
    setError("");
    setSuccessMessage("");
    if (editing) {
      await EditUniversityUserCall(editing.id, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email,
      });
      setSuccessMessage("Professor updated.");
    } else {
      const res = await AddUniversityUsersCall({
        university_id: universityIdForAdd,
        email,
        role: "prof",
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
      });
      const data = res?.data ?? res;
      if (data?.created?.length > 0) {
        const item = data.created[0];
        if (item.email_sent) {
          setSuccessMessage("Professor registered. An email with login credentials was sent to them.");
        } else {
          setSuccessMessage(
            item.email_error
              ? `Professor registered, but the email could not be sent: ${item.email_error}`
              : "Professor registered. Check server configuration to enable invitation emails."
          );
        }
      } else if (data?.updated?.length > 0) {
        const item = data.updated[0];
        if (item.email_sent) {
          setSuccessMessage("User already exists. An invitation email was sent to them to join Microlab.");
        } else {
          setSuccessMessage(
            item.email_error
              ? `User added, but the invitation email could not be sent: ${item.email_error}`
              : "User added. Check server configuration to enable invitation emails."
          );
        }
      } else if (data?.invalid?.length > 0) {
        setError(data.invalid[0]?.reason || "Could not add professor.");
        return;
      } else {
        setSuccessMessage("Professor added.");
      }
    }
    closeDialog();
    await fetchProfessors();
  };

  const openDeleteDialog = (professor) => {
    setProfessorToDelete(professor);
    setDeleteDialogOpen(true);
    closeActionMenu();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProfessorToDelete(null);
  };

  const handleDelete = async () => {
    if (!professorToDelete) return;
    await RemoveUniversityUserCall(professorToDelete.id);
    await fetchProfessors();
    closeDeleteDialog();
  };

  const universityName =
    auth?.universityInfo?.name || auth?.universityInfo?.short_name || "your university";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width={40}
                    height={40}
                    borderRadius="lg"
                    sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  >
                    <Icon sx={{ color: "white", fontSize: 28 }}>school</Icon>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h6" color="white" fontWeight="bold">
                      Professors
                    </MDTypography>
                    {isUniversityAdmin && universityId ? (
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.95 }}>
                        Manage professors for {universityName}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.95 }}>
                        View, add, edit, and delete professors
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
                {isUniversityAdmin && universityId && (
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={openAddDialog}
                    sx={{ boxShadow: 1 }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: 20 }}>person_add</Icon>
                    Add professor
                  </MDButton>
                )}
              </MDBox>

              <MDBox pt={2} px={2} pb={2}>
                {error && (
                  <MDAlert color="error" dismissible onClose={() => setError("")} sx={{ mb: 2 }}>
                    {error}
                  </MDAlert>
                )}
                {successMessage && (
                  <MDAlert color="success" dismissible onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
                    {successMessage}
                  </MDAlert>
                )}

                {loading && <MDTypography variant="button">Loading...</MDTypography>}
                {!loading && (
                      <TableContainer>
                        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {professors.map((prof) => (
                              <TableRow key={prof.id}>
                                <TableCell>
                                  {prof.first_name || prof.last_name
                                    ? [prof.first_name, prof.last_name].filter(Boolean).join(" ")
                                    : prof.full_name || "—"}
                                </TableCell>
                                <TableCell>{prof.email || "—"}</TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => openActionMenu(e, prof)}
                                    aria-label="Actions"
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {professors.length === 0 && !loading && (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <MDBox py={4} textAlign="center">
                                    <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}>
                                      people_outline
                                    </Icon>
                                    <MDTypography variant="body2" color="text" display="block">
                                      No professors yet
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text" display="block" mb={2}>
                                      Add professors using the button above
                                    </MDTypography>
                                    {isUniversityAdmin && universityId && (
                                      <MDButton variant="gradient" color="info" size="small" onClick={openAddDialog}>
                                        Add your first professor
                                      </MDButton>
                                    )}
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                )}
                <Menu
                  anchorEl={actionMenuAnchor}
                  open={Boolean(actionMenuAnchor)}
                  onClose={closeActionMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => {
                      if (actionMenuProfessor) openEditDialog(actionMenuProfessor);
                      closeActionMenu();
                    }}
                  >
                    <i className="material-icons" style={{ marginRight: 8, fontSize: 20 }}>
                      edit
                    </i>
                    Edit
                  </MenuItem>
                  <MenuItem onClick={handleResendInvitation}>
                    <i className="material-icons" style={{ marginRight: 8, fontSize: 20 }}>
                      mail
                    </i>
                    Resend invitation
                  </MenuItem>
                  <MenuItem onClick={handleResendCredentials}>
                    <i className="material-icons" style={{ marginRight: 8, fontSize: 20 }}>
                      key
                    </i>
                    Resend credentials
                  </MenuItem>
                  <MenuItem
                    onClick={() => actionMenuProfessor && openDeleteDialog(actionMenuProfessor)}
                    sx={{ color: "error.main" }}
                  >
                    <i className="material-icons" style={{ marginRight: 8, fontSize: 20 }}>
                      delete
                    </i>
                    Delete
                  </MenuItem>
                </Menu>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
        <DialogTitle>{editing ? "Edit professor" : "Add professor"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            {!editing && !isUniversityAdmin && (
              <FormControl fullWidth size="small" required>
                <InputLabel>University</InputLabel>
                <Select
                  value={form.university_id}
                  label="University"
                  onChange={handleChange("university_id")}
                >
                  <MenuItem value="">
                    <em>Select university</em>
                  </MenuItem>
                  {universities.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name || u.short_name || u.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!editing && isUniversityAdmin && universityId && (
              <MDBox>
                <MDTypography variant="caption" color="text">
                  University: {universityName}
                </MDTypography>
              </MDBox>
            )}
            <TextField
              label="First name"
              value={form.first_name}
              onChange={handleChange("first_name")}
              fullWidth
              size="small"
            />
            <TextField
              label="Last name"
              value={form.last_name}
              onChange={handleChange("last_name")}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              size="small"
              required
              disabled={!!editing}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <MDButton variant="gradient" color="info" onClick={handleSubmit}>
            {editing ? "Save" : "Add"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
        <DialogTitle>Delete professor</DialogTitle>
        <DialogContent>
          {professorToDelete && (
            <MDTypography variant="body2" color="text">
              Are you sure you want to remove{" "}
              <strong>
                {professorToDelete.first_name || professorToDelete.last_name
                  ? [professorToDelete.first_name, professorToDelete.last_name].filter(Boolean).join(" ")
                  : professorToDelete.full_name || professorToDelete.email}
              </strong>{" "}
              from the professors list? This action cannot be undone.
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <MDButton variant="gradient" color="error" onClick={handleDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Professors;
